import { DateTime } from 'luxon';
import { Gender } from '@cf-shared/enums';
import { Log_WeighIn, getAverageWaist, getAverageCalipersSum } from '@cf-shared/data-model/log-weigh-in';
import { Log_DayStart } from '@cf-shared/data-model/log-day-start';
import { INFOODS } from '@cf-shared/data-model/food';

/** A unit conversion as returned by `getMeasureUnitConversion`: multiply by `factor`, label with `unitName`. */
export interface ProgressCsvUnitConversion {
  factor: number;
  unitName: string;
}

export interface ProgressCsvInput {
  dates: DateTime[];
  weighInsByDate: Map<string, Log_WeighIn>;
  dayStartsByDate: Map<string, Log_DayStart>;
  gender: Gender;
  weightConversion: ProgressCsvUnitConversion;
  tapeConversion: ProgressCsvUnitConversion;
}

/** Build the Progress page's CSV export: one row per date, empty cells where nothing was recorded. */
export function buildProgressCsv(input: ProgressCsvInput): string {
  const { dates, weighInsByDate, dayStartsByDate, gender, weightConversion, tapeConversion } = input;

  // build CSV array
  const csvArray: string[][] = [];

  // add header
  csvArray.push([
    'Date',
    `Weight [${weightConversion.unitName}]`,
    'Skinfolds [mm]',
    `Waist [${tapeConversion.unitName}]`,
    'Logged Calories [kcal]',
    'Target Calories [kcal]'
  ]);

  // add data rows
  for (const date of dates) {
    const dateStr = date.toISODate();
    const weighIn = weighInsByDate.get(dateStr);
    const dayStart = dayStartsByDate.get(dateStr);

    // weight
    const weight = weighIn?.weight_kg
      ? (weighIn.weight_kg * weightConversion.factor).toFixed(1)
      : '';

    // skinfolds (calipers)
    const calipersAvg = weighIn ? getAverageCalipersSum(weighIn, gender) : undefined;
    const calipers = calipersAvg ? calipersAvg.toFixed(1) : '';

    // waist (tape)
    const waistAvg = weighIn ? getAverageWaist(weighIn) : undefined;
    const waist = waistAvg ? (waistAvg * tapeConversion.factor).toFixed(1) : '';

    // calories: logged and target in separate columns, the same fields the Progress chart draws (#5536)
    const loggedCalories = formatCalories(dayStart?.nutrients?.[INFOODS.Calories]);
    const targetCalories = formatCalories(dayStart?.targets?.[INFOODS.Calories]?.target);

    csvArray.push([dateStr, weight, calipers, waist, loggedCalories, targetCalories]);
  }

  // convert to CSV string
  return csvArray.map(row => row.join(',')).join('\n');
}

/**
 * Round a calorie value to a whole number, as the chart does. A real 0 prints as `0`. A missing
 * value, or a wrong-typed one from an old or damaged day-start document, prints as an empty cell.
 */
function formatCalories(value: unknown): string {
  // if the value is not a usable number, leave the cell empty
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  return Math.round(value).toString();
}
